// src/pages/match/ResultsPage.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { api, atlasGeneric } from '../../api'
import { PROFESSIONS, resolveProfessionParam } from '../../professions'
import Spinner from '../../components/Spinner'
import { RatingStarsDisplay } from '../../components/RatingStars'
import { useAccountAvatar } from '../../contexts/AccountAvatarContext'
import Turnstile from '../../components/Turnstile'
import { auth } from '../../firebase'
import { CheckCircle2, Clock } from 'lucide-react'
import { detectTimeZone, getCountryLabel, normalizeCountryCode } from '../../countryRules'

const maskEmail = (value: string) => {
  const trimmed = value.trim()
  const parts = trimmed.split('@')
  if (parts.length !== 2) return ''
  const [local, domain] = parts
  if (!local || !domain) return ''
  const first = local.slice(0, 1)
  const maskedLocal = local.length > 1 ? `${first}***` : '*'
  return `${maskedLocal}@${domain}`
}

function PayInline({
  picks,
  specialty,
  prefillReason,
  onClose,
  onSuccess,
  profession,
  searchState,
}: {
  picks: { specialistId: string; slotISO: string }[]
  specialty: string
  prefillReason?: string
  onClose: () => void
  onSuccess: (id: string) => void
  profession: ReturnType<typeof resolveProfessionParam>
  searchState?: string
}) {
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState<string | null>(null)
  const [note, setNote] = React.useState(prefillReason || '')
  const guestEligible = !auth.currentUser
  const [guest, setGuest] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  })
  const [turnstileToken, setTurnstileToken] = React.useState('')
  const [showTurnstile, setShowTurnstile] = React.useState(false)
  const [waitingTurnstile, setWaitingTurnstile] = React.useState(false)
  const [successState, setSuccessState] = React.useState<{
    selectionId: string
    isGuest: boolean
    emailMasked?: string
  } | null>(null)

  const submit = async () => {
    setErr(null)
    const clientTimeZone = detectTimeZone()
    const trimmedNote = note.trim()
    if (!trimmedNote) {
      setErr('Please add a brief note for your request.')
      return
    }
    if (guestEligible) {
      if (
        !guest.firstName.trim() ||
        !guest.lastName.trim() ||
        !guest.email.trim()
      ) {
        setErr('Please fill all required fields.')
        return
      }
      if (!turnstileToken) {
        setShowTurnstile(true)
        setWaitingTurnstile(true)
        return
      }
    }
    setBusy(true)
    try {
      if (guestEligible) {
        const selectionRes = await api.createGuestSelection({
          profession,
          specialty: specialty || 'Direct booking',
          reason: trimmedNote,
          picks,
          clientTimeZone,
          autoNotifyTop: !picks.length,
          state: searchState || undefined,
          firstName: guest.firstName.trim(),
          lastName: guest.lastName.trim(),
          email: guest.email.trim(),
          phone: guest.phone.trim() || undefined,
          turnstileToken,
        })
        const selectionId =
          selectionRes?.selectionId ||
          selectionRes?.id ||
          selectionRes?.data?.selectionId ||
          ''
        if (!selectionId) throw new Error('No selectionId returned')
        setSuccessState({
          selectionId,
          isGuest: true,
          emailMasked: maskEmail(guest.email.trim()),
        })
        return
      }

      const selectionRes = await api.createSelection({
        profession,
        specialty: specialty || 'Direct booking',
        reason: trimmedNote,
        picks,
        clientTimeZone,
        autoNotifyTop: !picks.length,
        state: searchState || undefined,
      })
      const selectionId =
        selectionRes?.selectionId ||
        selectionRes?.id ||
        selectionRes?.data?.selectionId ||
        ''
      if (!selectionId) throw new Error('No selectionId returned')
      setSuccessState({ selectionId, isGuest: false })
    } catch (e: any) {
      setErr(e?.message || 'Failed to submit request')
      if (guestEligible) {
        setTurnstileToken('')
      }
    } finally {
      setBusy(false)
      setWaitingTurnstile(false)
    }
  }

  React.useEffect(() => {
    if (!guestEligible) return
    if (waitingTurnstile && turnstileToken && !busy) {
      submit()
    }
  }, [waitingTurnstile, turnstileToken, busy, guestEligible])

  return (
    <div className="overlay show" onClick={onClose}>
      <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="sheet-header">
          <div className="sheet-title">{successState ? 'Request sent' : 'Submit request'}</div>
          <button className="btn close" onClick={onClose}>✕</button>
        </div>
        <div className="sheet-body">
          {err && <div className="banner error" role="alert">{err}</div>}
          <div className="card" style={{ padding: '1rem', gap: '0.75rem', display: 'flex', flexDirection: 'column' }}>
          {successState ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <CheckCircle2 size={56} color="#2e7d32" strokeWidth={1.6} />
                  <h3 style={{ margin: 0 }}>Request sent</h3>
                  <p className="hint" style={{ margin: 0, textAlign: 'center' }}>
                    Thanks for submitting your request. We’ve notified the top matching professionals.
                  </p>
                </div>
                <div className="card" style={{ padding: '0.9rem 1rem' }}>
                  <div className="field-label" style={{ marginBottom: 6 }}>What happens next</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li>Professionals typically respond within 24 hours.</li>
                    <li>If your request is still unpaid in 2 hours, we’ll prompt you to send it to another 3 professionals.</li>
                  </ul>
                </div>
                {successState.isGuest ? (
                  <div className="card" style={{ padding: '0.9rem 1rem' }}>
                    <div className="field-label" style={{ marginBottom: 6 }}>Secure access link</div>
                    <p className="hint" style={{ margin: 0 }}>
                      We emailed a secure link to {successState.emailMasked || 'your email'}.
                    </p>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <p className="hint">No payment is required to send your request.</p>
                {guestEligible && (
                  <>
                    <div className="row">
                      <div className="col">
                        <label className="field-label">
                          First name <span style={{ color: '#dc2626' }}>*</span>
                          <input
                            className="input"
                            value={guest.firstName}
                            onChange={(e) => setGuest(prev => ({ ...prev, firstName: e.target.value }))}
                          />
                        </label>
                      </div>
                      <div className="col">
                        <label className="field-label">
                          Last name <span style={{ color: '#dc2626' }}>*</span>
                          <input
                            className="input"
                            value={guest.lastName}
                            onChange={(e) => setGuest(prev => ({ ...prev, lastName: e.target.value }))}
                          />
                        </label>
                      </div>
                    </div>
                    <label className="field-label">
                      Mobile number (optional)
                      <input
                        className="input"
                        value={guest.phone}
                        onChange={(e) => setGuest(prev => ({ ...prev, phone: e.target.value }))}
                      />
                    </label>
                    <label className="field-label">
                      Email <span style={{ color: '#dc2626' }}>*</span>
                      <input
                        className="input"
                        value={guest.email}
                        onChange={(e) => setGuest(prev => ({ ...prev, email: e.target.value }))}
                      />
                    </label>
                  </>
                )}
                <div>
                  <div className="field-label">What do you need and when?</div>
                  <textarea
                    className="input"
                    style={{ minHeight: 90 }}
                    maxLength={600}
                    placeholder="Add a brief note for the professional"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    required
                  />
                </div>
              </>
            )}
          </div>

          {guestEligible && !successState && import.meta.env.VITE_TURNSTILE_SITE_KEY && showTurnstile && (
            <div className="field" style={{ marginTop: 8 }}>
              <div className="field-label">Quick verification</div>
              <Turnstile
                siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                action="guest_request"
                onToken={(token) => {
                  setTurnstileToken(token)
                  if (token) {
                    setShowTurnstile(false)
                  }
                  if (!token) {
                    setWaitingTurnstile(false)
                    setBusy(false)
                  }
                }}
              />
            </div>
          )}
        </div>
        <div className="sheet-footer">
          {successState ? (
            successState.isGuest ? (
              <button className="btn" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => successState?.selectionId && onSuccess(successState.selectionId)}
                >
                  View request
                </button>
                <a className="btn btn-outline" href="/clients">
                  Back to dashboard
                </a>
              </>
            )
          ) : (
            <>
              <button className="btn" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={submit} disabled={busy || waitingTurnstile}>
                {busy ? 'Submitting…' : waitingTurnstile ? 'Verifying…' : 'Submit request'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

type SortKey = 'recommended' | 'rating' | 'availability' | 'price'

type Specialist = {
  specialistId: string
  name: string
  title?: string | null
  photoUrl?: string
  profile?: string
  subspecialties?: string[]
  specialties?: string[]
  specialty?: string
  profession?: string
  address?: { city?: string; state?: string; country?: string; countryCode?: string }
  city?: string | null
  state?: string
  ratingAverage?: number | null
  ratingCount?: number | null
  price?: number | null
  pricing?: {
    minAud?: number
    maxAud?: number
    currency?: string
    medicareRebate?: boolean
    notes?: string | null
  } | null
  score?: number
  hasAvailability?: boolean
}

type PickState = {
  specialistId: string
  chosenSlotISO?: string
  slots: string[]
  loading: boolean
  error?: string | null
}

const MAX_SELECT = 3
const MIN_FOLLOWUP_EXACT_MATCHES = 3
const BOTTOM_DOCK_OFFSET = 72
const CONTACT_SLOT = 'REQUEST_BOOKING'
const PROFILE_REVIEWS_PAGE_SIZE = 10
const PROFILE_REVIEWS_FETCH_LIMIT = 50
const MAX_PROFILE_REVIEWS = 500
const SEARCH_PUBLIC_API = (
  import.meta.env.VITE_SEARCH_API_BASE ||
  import.meta.env.VITE_API_BASE ||
  'https://54uqjwvuke.execute-api.ap-southeast-2.amazonaws.com/prod'
).replace(/\/+$/, '')
const BLOCKED_PROVIDER_REDIRECTS: Record<string, string> = {
  // Dr Lily Vu
  bep3A8SsfmXCNDP8wtaqzKBjcJc2: 'https://eucamd.com/booking',
  // Dr Dong Zhao
  fUzzUrZrOfRqcrzXPDCOH59Gzja2: 'https://eucamd.com/booking',
}

const normalizeLabel = (value: string) => value.toLowerCase().replace(/\s+/g, ' ').trim()
const dedupeLocationParts = (parts: Array<unknown>) => {
  const seen = new Set<string>()
  const out: string[] = []
  for (const part of parts) {
    const value = String(part || '').trim()
    if (!value) continue
    const key = value.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(value)
  }
  return out
}

const formatPricingRange = (min?: number, max?: number, currency?: string | null) => {
  if (typeof min !== 'number' || typeof max !== 'number') return null
  const currencyCode =
    typeof currency === 'string' && currency.trim()
      ? currency.trim().toUpperCase()
      : 'AUD'
  const minLabel = min === 0 ? 'Contact for quote' : `$${min.toFixed(2)} ${currencyCode}`
  const maxLabel = `$${max.toFixed(2)} ${currencyCode}`
  if (min === 0) return max > 0 ? `${minLabel} – ${maxLabel}` : minLabel
  return `${minLabel} – ${maxLabel}`
}

const parseReviewDate = (value?: string | null) => {
  if (!value) return null
  const trimmed = String(value).trim()
  if (!trimmed || trimmed === CONTACT_SLOT) return null
  if (/^\d+$/.test(trimmed)) {
    const raw = Number(trimmed)
    const ms = trimmed.length <= 10 ? raw * 1000 : raw
    const parsed = new Date(ms)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatReviewDate = (slotISO?: string | null, createdAt?: string | null, timeZone: string = detectTimeZone()) => {
  const parsed = parseReviewDate(slotISO) || parseReviewDate(createdAt)
  if (!parsed) return ''
  return parsed.toLocaleDateString('en-AU', {
    timeZone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const sortReviewsByDate = (reviews: any[]) =>
  [...reviews].sort((a, b) => {
    const aDate = parseReviewDate(a?.slotISO) || parseReviewDate(a?.createdAt)
    const bDate = parseReviewDate(b?.slotISO) || parseReviewDate(b?.createdAt)
    const aTime = aDate ? aDate.getTime() : 0
    const bTime = bDate ? bDate.getTime() : 0
    return bTime - aTime
  })

export default function ResultsPage() {
  const { profession: raw } = useParams()
  const profession = resolveProfessionParam(raw)
  const cfg = PROFESSIONS[profession]

  const nav = useNavigate()
  const [qp, setSearchParams] = useSearchParams()

  const providerId = qp.get('providerId') || ''
  const providerName = qp.get('providerName') || ''
  const providerImage = qp.get('providerImage') || ''
  const preferredSlot = qp.get('slot') || ''
  const specialty = qp.get('specialty') || (providerId ? 'Direct booking' : '')
  const reason = qp.get('reason') || ''
  const state = qp.get('state') || ''
  const followupFlag = qp.get('followup') || ''
  const aiRequest = qp.get('aiRequest') === '1'
  const excludeProviderId = qp.get('excludeProviderId') || ''
  const displayTz =
    qp.get('tz') || detectTimeZone()
  const allowedSorts: SortKey[] = ['recommended', 'rating', 'availability', 'price']
  const rawSort = qp.get('sort') as SortKey | null
  const sortKey: SortKey = rawSort && allowedSorts.includes(rawSort) ? rawSort : 'recommended'
  const sortOptions: Array<{ key: SortKey; label: string }> = [
    { key: 'recommended', label: 'Recommended' },
    { key: 'rating', label: 'Highest rating' },
    { key: 'availability', label: 'Earliest availability' },
    { key: 'price', label: 'Lowest fee' },
  ]

  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [specialists, setSpecialists] = useState<Specialist[]>([])
  const [picks, setPicks] = useState<Record<string, PickState>>({})
  const autoOpenedRequestRef = useRef(false)
  const computeSlotsPerRow = (w: number) => {
    if (!Number.isFinite(w) || w <= 0) return 4
    if (w < 360) return 1
    if (w < 540) return 2
    if (w < 768) return 3
    return 4
  }

  const [slotsPerRow, setSlotsPerRow] = useState(() => {
    if (typeof window === 'undefined') return 4
    return computeSlotsPerRow(window.innerWidth)
  })

  const [slotOffsets, setSlotOffsets] = useState<Record<string, number>>({})
  const [submitting, setSubmitting] = useState(false)
  const stickyRef = useRef<HTMLDivElement | null>(null)
  const [payDraft, setPayDraft] = useState<{ picks: { specialistId: string; slotISO: string }[] } | null>(null)
  const [activeSpecialist, setActiveSpecialist] = useState<Specialist | null>(null)
  const [profileReviews, setProfileReviews] = useState<any[]>([])
  const { accountAvatarUrl, accountUserId } = useAccountAvatar()
  const [profileReviewsLoading, setProfileReviewsLoading] = useState(false)
  const [profileReviewsError, setProfileReviewsError] = useState<string | null>(null)
  const [profileReviewsVisibleCount, setProfileReviewsVisibleCount] = useState(PROFILE_REVIEWS_PAGE_SIZE)
  const profileReviewsSentinelRef = useRef<HTMLDivElement | null>(null)
  const [showJumpHint, setShowJumpHint] = useState(false)
  const [followupUsesFallback, setFollowupUsesFallback] = useState(false)
  const [showFollowupOverlay, setShowFollowupOverlay] = useState(false)
  const isDirectBooking = Boolean(providerId)
  const allowListMode = !providerId && followupFlag === '1'
  const followupLabel = specialty.trim()
  const followupOverlayKey = allowListMode
    ? `followup-overlay:${profession}:${normalizeLabel(followupLabel)}:${excludeProviderId || 'none'}`
    : ''

  const redirectIfBlocked = (id: string) => {
    const target = BLOCKED_PROVIDER_REDIRECTS[id]
    if (target && typeof window !== 'undefined') {
      window.location.href = target
      return true
    }
    return false
  }

  const resolvePhotoSrc = (s: Specialist) => {
    const ownsProfile = s.specialistId === accountUserId
    return s.photoUrl || (ownsProfile && accountAvatarUrl ? accountAvatarUrl : null) || 'https://placehold.co/80x80'
  }

  // guard: direct bookings only
  useEffect(() => {
    if (providerId || allowListMode) return
    const params = new URLSearchParams()
    if (raw) params.set('profession', profession)
    nav(`/clients${params.toString() ? `?${params.toString()}` : ''}`, { replace: true })
  }, [providerId, allowListMode, nav, profession, raw])

  useEffect(() => {
    if (!allowListMode || typeof window === 'undefined') {
      setShowFollowupOverlay(false)
      return
    }
    const dismissed = sessionStorage.getItem(followupOverlayKey) === '1'
    setShowFollowupOverlay(!dismissed)
  }, [allowListMode, followupOverlayKey])

  // fetch professionals by criteria (profession-aware)
  useEffect(() => {
    let cancelled = false
    const mapPublicSearchItem = (item: any): Specialist | null => {
      const id = item?.userId || item?.specialistId || ''
      if (!id) return null
      const rating =
        typeof item.rating === 'number'
          ? item.rating
          : typeof item.ratingAverage === 'number'
            ? item.ratingAverage
            : typeof item.rating === 'string'
              ? Number(item.rating)
              : typeof item.ratingAverage === 'string'
                ? Number(item.ratingAverage)
                : null
      const ratingCount =
        typeof item.ratingCount === 'number'
          ? item.ratingCount
          : typeof item.reviewCount === 'number'
            ? item.reviewCount
            : typeof item.ratingCount === 'string'
              ? Number(item.ratingCount)
              : null
      const image =
        item.image ||
        item.photoPublicUrl ||
        item.photoUrl ||
        item.photo ||
        null
      return {
        specialistId: id,
        name: item.name || item.profession || 'Professional',
        photoUrl: image || undefined,
        specialty: item.displayProfession || item.profession || specialty || '',
        specialties: Array.isArray(item.specialties) ? item.specialties : [],
        ratingAverage: Number.isFinite(Number(rating)) ? Number(rating) : null,
        ratingCount: Number.isFinite(Number(ratingCount)) ? Number(ratingCount) : null,
        pricing: item.pricing || null,
        state: item.state || null,
        profile: item.profile || '',
        score: typeof item.score === 'number' ? item.score : undefined,
      }
    }
    const fetchPublicSearchResults = async () => {
      if (!SEARCH_PUBLIC_API) return []
      const url = new URL(`${SEARCH_PUBLIC_API}/public/search`)
      if (specialty.trim()) url.searchParams.set('q', specialty.trim())
      if (profession) url.searchParams.set('profession', profession)
      const res = await fetch(url.toString(), { cache: 'no-store' })
      if (!res.ok) throw new Error('Failed to load specialists')
      const json = await res.json()
      return Array.isArray(json?.items) ? json.items : []
    }
    const load = async () => {
      setLoading(true); setErr(null); setPicks({}); setSpecialists([])

      // Direct booking: single provider, but still fetch availability to prefer real slots when present
      if (providerId) {
        if (redirectIfBlocked(providerId)) return
        const single: Specialist = {
          specialistId: providerId,
          name: providerName || 'Preferred provider',
          photoUrl: providerImage || undefined,
          specialty: specialty || 'Direct booking',
          specialties: [specialty || 'Direct booking'],
          ratingAverage: null,
          ratingCount: null,
          price: null,
          city: null,
          state: null,
          profile: reason || '',
        }
        setSpecialists([single])
        setPicks({
          [providerId]: {
            specialistId: providerId,
            slots: [],
            chosenSlotISO: preferredSlot || undefined,
            loading: true,
          },
        })
        try {
          const slotsRes = await atlasGeneric.availabilitySlots(profession, providerId, 30)
          const slots = Array.isArray(slotsRes?.slots) ? (slotsRes.slots as string[]) : []
          if (cancelled) return
          const initialSlot =
            (preferredSlot && slots.includes(preferredSlot)) ? preferredSlot
              : slots.length ? slots[0]
                : CONTACT_SLOT
          setPicks(prev => ({
            ...prev,
            [providerId]: {
              ...prev[providerId],
              slots,
              loading: false,
              error: undefined,
              chosenSlotISO: initialSlot,
            },
          }))
          setSpecialists([{ ...single, hasAvailability: slots.length > 0 }])
        } catch (e: any) {
          if (cancelled) return
          setPicks(prev => ({
            ...prev,
            [providerId]: {
              ...prev[providerId],
              loading: false,
              error: e?.message || 'Failed to load slots',
              chosenSlotISO: CONTACT_SLOT,
            },
          }))
          setSpecialists([{ ...single, hasAvailability: false }])
        }
        setLoading(false)
        return
      }

      let attempt = 0
      while (attempt < 2 && !cancelled) {
        try {
          let items: Specialist[] = []
          if (allowListMode) {
            const res = await fetchPublicSearchResults()
            const mapped = res.map(mapPublicSearchItem).filter(Boolean) as Specialist[]
            const labelNormalized = normalizeLabel(specialty)
            const base = excludeProviderId
              ? mapped.filter((item) => item.specialistId !== excludeProviderId)
              : mapped
            if (labelNormalized) {
              const exact = base.filter(
                (item) => normalizeLabel(item.specialty || '') === labelNormalized
              )
              const fallback = base.filter(
                (item) => normalizeLabel(item.specialty || '') !== labelNormalized
              )
              const useFallback = exact.length < MIN_FOLLOWUP_EXACT_MATCHES
              items = useFallback ? [...exact, ...fallback] : exact
              setFollowupUsesFallback(useFallback)
            } else {
              items = base
              setFollowupUsesFallback(false)
            }
          } else {
            setFollowupUsesFallback(false)
            const res = await atlasGeneric.searchProfessionals({
              profession,
              specialty,
              reason: reason || undefined,
              state: state || undefined,
              limit: 30,
              sort: sortKey,
            })
            items = Array.isArray(res?.items) ? res.items : (res || [])
            if (excludeProviderId) {
              items = items.filter((item) => item.specialistId !== excludeProviderId)
            }
          }
          if (cancelled) return
          setSpecialists(items)

          const base: Record<string, PickState> = {}
          for (const s of items) base[s.specialistId] = { specialistId: s.specialistId, slots: [], loading: true }
          setPicks(base)

          // fetch availability (30 days, 30-min slots). limit concurrency to 5.
          const queue = [...items]
          const run = async () => {
            const s = queue.shift()
            if (!s || cancelled) return
            try {
              const slotsRes = await atlasGeneric.availabilitySlots(profession, s.specialistId, 30)
              const slots = Array.isArray(slotsRes?.slots) ? (slotsRes.slots as string[]) : []
              if (cancelled) return
              setPicks(prev => ({
                ...prev,
                [s.specialistId]: { ...prev[s.specialistId], slots, loading:false, error: undefined }
              }))
            } catch (e:any) {
              if (cancelled) return
              setPicks(prev => ({
                ...prev,
                [s.specialistId]: {
                  ...prev[s.specialistId],
                  loading:false,
                  error: (e?.message || 'Failed to load slots')
                }
              }))
            }
            await run()
          }
          await Promise.all(Array.from({ length: 5 }).map(run))
          if (!cancelled) setLoading(false)
          return
        } catch (e:any) {
          attempt += 1
          if (attempt >= 2 && !cancelled) {
            setErr(e?.message || 'Failed to load specialists')
          }
        }
      }
      if (!cancelled) setLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profession, specialty, reason, state, sortKey, providerId, providerName, providerImage, allowListMode, excludeProviderId])

  const chosenCount = useMemo(
    () => Object.values(picks).filter(p => !!p.chosenSlotISO).length,
    [picks]
  )

  const sortedSpecialists = useMemo(() => {
    if (isDirectBooking) return specialists
    const list = [...specialists]
    const bumpAvailability = (arr: Specialist[]) => {
      const reviewed: Specialist[] = []
      const avail: Specialist[] = []
      const rest: Specialist[] = []
      const earliestSlotTime = (sp: Specialist) => {
        const slotList = picks[sp.specialistId]?.slots || []
        if (slotList.length) {
          const ts = Date.parse(slotList[0])
          return Number.isFinite(ts) ? ts : Number.POSITIVE_INFINITY
        }
        return Number.POSITIVE_INFINITY
      }
      for (const s of arr) {
        const hasReviews = Number(s.ratingCount || 0) > 0 && Number(s.ratingAverage || 0) > 0
        const hasSlots = Boolean(picks[s.specialistId]?.slots?.length || s.hasAvailability)
        if (hasReviews) {
          reviewed.push(s)
          continue
        }
        if (hasSlots) {
          avail.push(s)
        } else {
          rest.push(s)
        }
      }
      const availSorted = avail.sort((a, b) => earliestSlotTime(a) - earliestSlotTime(b))
      return [...reviewed, ...availSorted, ...rest]
    }
    const compareNumbers = (a?: number | null, b?: number | null) => {
      const av = typeof a === 'number' ? a : 0
      const bv = typeof b === 'number' ? b : 0
      if (bv !== av) return bv - av
      return 0
    }
    const reorderFinancial = (list: Specialist[]) => {
      const CPA_TOKENS = ['elaine vu', 'josh pham', 'keerat']
      const ACCOUNTANT_TOKENS = ['joe scoulou', 'ha pham']
      const nameHas = (name: string, tokens: string[]) => tokens.some((t) => name.includes(t))
      const weight = (s: Specialist) => {
        const name = (s.name || '').toLowerCase().trim()
        const isFinancial =
          (s.profession || '').toLowerCase().includes('financial') ||
          (s.specialties || []).some((sp) => {
            const low = String(sp).toLowerCase()
            return low.includes('tax') || low.includes('account') || low.includes('finance')
          })
        if (!isFinancial) return 3
        if (nameHas(name, CPA_TOKENS)) return 0
        if (nameHas(name, ACCOUNTANT_TOKENS)) return 1
        return 2
      }
      return [...list]
        .map((s, idx) => ({ s, idx, w: weight(s) }))
        .sort((a, b) => (a.w !== b.w ? a.w - b.w : a.idx - b.idx))
        .map((x) => x.s)
    }
    const reorderLegal = (list: Specialist[]) => {
      const priorities = [
        'principal lawyer',
        'governance professional',
        'paralegal',
        'legal assistant',
        'naati translator',
      ]
      const weight = (s: Specialist) => {
        const label = (s.profession || s.specialty || '').toLowerCase().trim()
        const idx = priorities.findIndex((t) => label.includes(t))
        return idx === -1 ? priorities.length : idx
      }
      return [...list]
        .map((s, idx) => ({ s, idx, w: weight(s) }))
        .sort((a, b) => (a.w !== b.w ? a.w - b.w : a.idx - b.idx))
        .map((x) => x.s)
    }
    const earliestSlot = (id: string) => {
      const slots = picks[id]?.slots || []
      if (!slots.length) return Number.POSITIVE_INFINITY
      const ts = Date.parse(slots[0])
      return Number.isFinite(ts) ? ts : Number.POSITIVE_INFINITY
    }

    switch (sortKey) {
      case 'rating':
        return list.sort((a, b) => {
          const avgDiff = compareNumbers(a.ratingAverage, b.ratingAverage)
          if (avgDiff !== 0) return avgDiff
          return compareNumbers(a.ratingCount, b.ratingCount)
        })
      case 'availability':
        return list.sort((a, b) => earliestSlot(a.specialistId) - earliestSlot(b.specialistId))
      case 'price':
        return list.sort((a, b) => {
          const minA = typeof a.pricing?.minAud === 'number' ? a.pricing.minAud : Number.POSITIVE_INFINITY
          const minB = typeof b.pricing?.minAud === 'number' ? b.pricing.minAud : Number.POSITIVE_INFINITY
          if (minA !== minB) return minA - minB
          const maxA = typeof a.pricing?.maxAud === 'number' ? a.pricing.maxAud : Number.POSITIVE_INFINITY
          const maxB = typeof b.pricing?.maxAud === 'number' ? b.pricing.maxAud : Number.POSITIVE_INFINITY
          return maxA - maxB
        })
      case 'recommended':
      default:
        const sorted = list.sort((a, b) => {
          const scoreA = typeof a.score === 'number' ? a.score : 0
          const scoreB = typeof b.score === 'number' ? b.score : 0
          if (scoreB !== scoreA) return scoreB - scoreA
          return compareNumbers(a.ratingAverage, b.ratingAverage)
        })
        const bumped = bumpAvailability(sorted)
        if (profession === 'financial') return reorderFinancial(bumped)
        if (profession === 'legal') return reorderLegal(bumped)
        return bumped
    }
  }, [specialists, sortKey, picks, isDirectBooking])

  const primarySpecialist = isDirectBooking
    ? sortedSpecialists.find(s => s.specialistId === providerId) || null
    : null
  const alternateSpecialists = isDirectBooking ? [] : sortedSpecialists
  const canSubmit = isDirectBooking
    ? chosenCount > 0
    : (!loading && sortedSpecialists.length > 0)

  const renderName = (spec: Specialist) =>
    [spec.name || spec.specialistId].filter(Boolean).join(' ').trim() ||
    spec.name ||
    spec.specialistId

  const localLabel = (iso: string) => {
    if (iso === CONTACT_SLOT) return 'Request booking'
    try {
      return new Date(iso).toLocaleString('en-AU', {
        timeZone: displayTz,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
      })
    } catch { return new Date(iso).toLocaleString() }
  }

  const toggleSlot = (specialistId: string, iso: string) => {
    if (redirectIfBlocked(specialistId)) return
    setPicks(prev => {
      const p = prev[specialistId]
      if (!p) return prev
      const already = p.chosenSlotISO === iso
      if (already) return { ...prev, [specialistId]: { ...p, chosenSlotISO: undefined } }
      if (!already && chosenCount >= MAX_SELECT && !p.chosenSlotISO) return prev
      return { ...prev, [specialistId]: { ...p, chosenSlotISO: iso } }
    })
  }
  const shiftSlotPage = (specialistId: string, delta: number, totalSlots: number) => {
    const pageSize = slotsPerRow
    if (!totalSlots || totalSlots <= pageSize) return
    setSlotOffsets(prev => {
      const current = prev[specialistId] || 0
      const maxOffset = Math.max(0, totalSlots - pageSize)
      const next = Math.max(0, Math.min(current + delta * pageSize, maxOffset))
      if (next === current) return prev
      return { ...prev, [specialistId]: next }
    })
  }

  const submit = async () => {
    setErr(null)
    if (!specialty && !providerId) { setErr('Missing specialty'); return }
    let picksArr = Object.values(picks).filter(p => !!p.chosenSlotISO)
      .map(p => ({ specialistId: p.specialistId, slotISO: p.chosenSlotISO! }))
    if (providerId) {
      const hasSlots = Array.isArray(picks[providerId]?.slots) && picks[providerId]?.slots.length > 0
      if (!picksArr.length) {
        if (hasSlots) {
          setErr('Pick a time to continue.')
          return
        }
        picksArr = [{ specialistId: providerId, slotISO: CONTACT_SLOT }]
      }
    } else if (picksArr.length === 0) {
      // Let backend auto-notify top matches when no manual picks are supplied.
      picksArr = []
    }

    setSubmitting(true)
    setPayDraft({ picks: picksArr })
    setSubmitting(false)
  }

  useEffect(() => {
    if (!isDirectBooking || !providerId) return
    if (autoOpenedRequestRef.current || payDraft) return
    const pick = picks[providerId]
    if (!pick || pick.loading) return
    let slotISO = pick.chosenSlotISO
    if (!slotISO && Array.isArray(pick.slots) && !pick.slots.length) {
      slotISO = CONTACT_SLOT
    }
    if (!slotISO) return
    autoOpenedRequestRef.current = true
    setPayDraft({ picks: [{ specialistId: providerId, slotISO }] })
  }, [isDirectBooking, providerId, picks, payDraft])

  useEffect(() => {
    if (isDirectBooking || !aiRequest) return
    if (autoOpenedRequestRef.current || payDraft || loading) return
    if (!sortedSpecialists.length) return
    autoOpenedRequestRef.current = true
    setPayDraft({ picks: [] })
    const nextParams = new URLSearchParams(qp)
    nextParams.delete('aiRequest')
    setSearchParams(nextParams, { replace: true })
  }, [isDirectBooking, aiRequest, payDraft, loading, sortedSpecialists.length, qp, setSearchParams])

  useEffect(() => {
    if (!activeSpecialist) {
      setProfileReviews([])
      setProfileReviewsError(null)
      setProfileReviewsLoading(false)
      setProfileReviewsVisibleCount(PROFILE_REVIEWS_PAGE_SIZE)
      return
    }
    const count = Number(activeSpecialist.ratingCount || 0)
    if (!Number.isFinite(count) || count <= 0) {
      setProfileReviews([])
      setProfileReviewsError(null)
      setProfileReviewsLoading(false)
      setProfileReviewsVisibleCount(PROFILE_REVIEWS_PAGE_SIZE)
      return
    }
    let cancelled = false
    setProfileReviewsLoading(true)
    setProfileReviewsVisibleCount(0)
    setProfileReviewsError(null)
    ;(async () => {
      try {
        let cursor: string | null = null
        let all: any[] = []
        let pages = 0
        do {
          const res = await api.getSpecialistReviews(activeSpecialist.specialistId, profession, {
            cursor,
            limit: PROFILE_REVIEWS_FETCH_LIMIT,
          })
          const incoming = Array.isArray(res?.reviews) ? res.reviews : []
          all = all.concat(incoming)
          cursor = res?.nextCursor || null
          pages += 1
        } while (cursor && all.length < MAX_PROFILE_REVIEWS && pages < 20)
        if (cancelled) return
        const sorted = sortReviewsByDate(all)
        setProfileReviews(sorted)
        setProfileReviewsVisibleCount(Math.min(PROFILE_REVIEWS_PAGE_SIZE, sorted.length))
      } catch (err: any) {
        if (!cancelled) setProfileReviewsError(err?.message || 'Failed to load reviews')
      } finally {
        if (!cancelled) setProfileReviewsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activeSpecialist?.specialistId, activeSpecialist?.ratingCount, profession])

  const loadMoreProfileReviews = () => {
    if (profileReviewsVisibleCount >= profileReviews.length) return
    setProfileReviewsVisibleCount((count) =>
      Math.min(count + PROFILE_REVIEWS_PAGE_SIZE, profileReviews.length)
    )
  }

  useEffect(() => {
    if (!activeSpecialist) return
    if (profileReviewsVisibleCount >= profileReviews.length) return
    const target = profileReviewsSentinelRef.current
    if (!target) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMoreProfileReviews()
      },
      { rootMargin: '0px 0px 240px 0px', threshold: 0 }
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [activeSpecialist, profileReviews.length, profileReviewsVisibleCount, loadMoreProfileReviews])

  const applySort = (key: SortKey) => {
    if (key === sortKey) return
    const params = new URLSearchParams(qp)
    params.set('sort', key)
    setSearchParams(params)
  }

  useEffect(() => {
    const handler = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop || 0
      setShowJumpHint(chosenCount > 0 && scrollY > 600)
    }
    handler()
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [chosenCount])

  const scrollToSubmit = () => {
    stickyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }

  const renderSpecialistCard = (s: Specialist) => {
    const p = picks[s.specialistId]
    const primarySpecialty = s.specialty || s.specialties?.[0] || specialty
    const ratingValue = typeof s.ratingAverage === 'number' ? s.ratingAverage : Number(s.ratingAverage || 0)
    const ratingCount = typeof s.ratingCount === 'number' ? s.ratingCount : Number(s.ratingCount || 0)
    const showMedicareInfo = profession === 'doctor'
    const medicareRebate = s.pricing?.medicareRebate
    const medicareLabel = medicareRebate === true ? 'Yes' : medicareRebate === false ? 'No' : 'Not specified'
    const medicareClass =
      medicareRebate === true ? 'pill-positive' : medicareRebate === false ? 'pill-neutral' : 'pill-muted'
    const countryLabel = s.address?.country
      || (s.address?.countryCode ? getCountryLabel(normalizeCountryCode(s.address.countryCode)) : null)
    const locationLabel =
      dedupeLocationParts([s.address?.city, s.address?.state || s.state, countryLabel]).join(', ') || '—'
    const ownsProfile = Boolean(accountUserId && s.specialistId && s.specialistId === accountUserId)
    const photoSrc = resolvePhotoSrc(s)
    return (
      <div key={s.specialistId} className="card" style={{ marginBottom: '.75rem' }}>
        <div className="row" style={{ alignItems:'center', gap:12 }}>
          <button
            type="button"
            aria-label="View details"
            onClick={() => setActiveSpecialist(s)}
            style={{ border:'none', background:'transparent', padding:0, cursor:'pointer' }}
          >
            <img
              src={photoSrc}
              alt={`${s.name || 'Specialist'} portrait`}
              className="results-photo"
              loading="lazy"
            />
          </button>
          <div className="col">
            <div style={{ fontWeight: 700 }}>{renderName(s)}</div>
            <div className="hint">
              {primarySpecialty || locationLabel}
              {s.subspecialties?.length ? ` • ${s.subspecialties.join(', ')}` : ''}
            </div>
            <div className="results-meta-row">
              {ratingCount > 0 && ratingValue > 0 && (
                <div className="results-rating">
                  <RatingStarsDisplay value={ratingValue} showValue />
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => setActiveSpecialist(s)}
                  >
                    {`${ratingCount} review${ratingCount === 1 ? '' : 's'}`}
                  </button>
                </div>
              )}
              {showMedicareInfo && (
                <div className="results-status-pills">
                  <span className={`pill ${medicareClass}`}>Medicare rebate: {medicareLabel}</span>
                </div>
              )}
            </div>
            {s.pricing && (
              <div className="results-fee-row">
                <span className="hint">Fee range</span>
                {formatPricingRange(s.pricing.minAud, s.pricing.maxAud, s.pricing.currency) ? (
                  <>
                    <strong>
                      {formatPricingRange(s.pricing.minAud, s.pricing.maxAud, s.pricing.currency)}
                    </strong>{' '}
                    <span className="hint">(per 30 minutes)</span>
                  </>
                ) : (
                  <strong>—</strong>
                )}
              </div>
            )}
            {s.profile && (
              <div className="results-profile-row" style={{ marginTop: 6 }}>
                <span
                  className="minor results-profile results-profile--compact"
                  title={s.profile}
                >
                  {s.profile}
                </span>
                <button
                  type="button"
                  className="link-btn"
                  onClick={() => setActiveSpecialist(s)}
                >
                  View details
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          {p?.loading && <div className="hint">Loading slots…</div>}
          {p?.error && <div className="hint" style={{ color:'var(--error)' }}>{p.error}</div>}
          {!p?.loading && !p?.error && (p?.slots?.length ? (() => {
            const totalSlots = p.slots?.length || 0
            const pageSize = slotsPerRow
            const maxOffset = Math.max(0, totalSlots - pageSize)
            const storedOffset = slotOffsets[s.specialistId] || 0
            const offset = Math.min(storedOffset, maxOffset)
            const visible = p.slots.slice(offset, offset + pageSize)
            const canPrev = offset > 0
            const canNext = offset + pageSize < totalSlots
            return (
              <div className="slot-nav">
                <button
                  type="button"
                  className="btn btn-ghost btn-icon"
                  aria-label="Earlier slots"
                  disabled={!canPrev}
                  onClick={() => shiftSlotPage(s.specialistId, -1, totalSlots)}
                >
                  ‹
                </button>
                <div className="inline-gap-2" style={{ flexWrap:'wrap', flex:1 }}>
                  {visible.map(iso => {
                    const chosen = p.chosenSlotISO === iso
                    const disableNew = chosenCount >= MAX_SELECT && !chosen
                    return (
                      <button
                        key={iso}
                        className={`btn ${chosen ? 'btn-primary' : 'btn-outline'}`}
                        disabled={disableNew}
                        onClick={() => toggleSlot(s.specialistId, iso)}
                        title={localLabel(iso)}
                      >
                        {localLabel(iso)}
                      </button>
                    )
                  })}
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-icon"
                  aria-label="Later slots"
                  disabled={!canNext}
                  onClick={() => shiftSlotPage(s.specialistId, 1, totalSlots)}
                >
                  ›
                </button>
              </div>
            )
          })() : (
            <button
              type="button"
              className={`btn ${p?.chosenSlotISO === CONTACT_SLOT ? 'btn-primary' : 'btn-outline'}`}
              disabled={p?.chosenSlotISO !== CONTACT_SLOT && chosenCount >= MAX_SELECT}
              onClick={() => toggleSlot(s.specialistId, CONTACT_SLOT)}
            >
              {p?.chosenSlotISO === CONTACT_SLOT ? '✓ Selected' : 'Request match'}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="container page" style={{ paddingBottom: `${BOTTOM_DOCK_OFFSET + 96}px` }}>
      {showFollowupOverlay && (
        <div
          className="overlay show"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              width: 'min(520px, 100%)',
              padding: '1.5rem 1.25rem',
              boxShadow: '0 20px 60px rgba(15,23,42,.25)',
              textAlign: 'center',
            }}
          >
            <Clock size={56} color="#2e7d32" strokeWidth={1.6} />
            <h3 style={{ margin: '0.75rem 0 0.4rem' }}>Would you like more professionals?</h3>
            <p className="hint" style={{ margin: 0 }}>
              Your current request stays open. You can also notify more professionals to increase your options.
            </p>
            {followupLabel ? (
              <p className="hint" style={{ margin: '0.6rem 0 0' }}>
                {followupUsesFallback
                  ? `We found other ${followupLabel} options plus similar ${cfg.label.toLowerCase()}.`
                  : `We found other ${followupLabel} options you can request.`}
              </p>
            ) : null}
            <button
              className="btn btn-primary"
              style={{ marginTop: '1.2rem' }}
              onClick={() => {
                if (typeof window !== 'undefined') {
                  sessionStorage.setItem(followupOverlayKey, '1')
                }
                setShowFollowupOverlay(false)
              }}
            >
              Show other professionals
            </button>
          </div>
        </div>
      )}
      <div className="header">
        <h2 style={{ margin: 0 }}>
          {specialty ? `${cfg.resultsLabel} — ${specialty}` : cfg.resultsLabel}
        </h2>
        <button className="btn" onClick={() => nav('/clients')}>Change criteria</button>
      </div>

      <div className="hint" style={{ marginBottom: 8 }}>
        Showing times in <b>{displayTz}</b>. You can change this on the previous step.
      </div>

      {err && <div className="banner error">{err}</div>}

      {!isDirectBooking && (
        <div className="results-sort-controls">
          <span className="hint">Sort by</span>
          <div className="chips">
            {sortOptions.map(opt => (
              <button
                key={opt.key}
                type="button"
                className={`chip ${sortKey === opt.key ? 'is-active' : ''}`}
                aria-pressed={sortKey === opt.key}
                onClick={() => applySort(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="results-list" style={{ paddingBottom: 24 }}>
        {loading && (
          <div className="results-spinner">
            <Spinner label="Loading results…" />
          </div>
        )}

        {!loading && !specialists.length && !err && (
          <div className="card"><p>No specialists matched your criteria.</p></div>
        )}

      {!loading && primarySpecialist && renderSpecialistCard(primarySpecialist)}

      {!loading && alternateSpecialists.map(s => renderSpecialistCard(s))}
      </div>

      {/* Sticky submit bar */}
      <div
        ref={stickyRef}
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          background: 'var(--surface, #fff)',
          borderTop: '1px solid var(--border, #e5e7eb)',
          marginTop: '1rem',
          boxShadow: '0 -10px 20px rgba(15,23,42,0.08)',
        }}
      >
        <div
          className="container"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '.6rem .25rem',
            paddingBottom: `${BOTTOM_DOCK_OFFSET + 12}px`,
          }}
        >
          <div className="hint" aria-live="polite">
            {isDirectBooking
              ? (chosenCount === 0
                ? 'Select a time slot or request booking to continue.'
                : 'Ready to send your request.')
              : 'Top 3 matching professionals will be notified automatically.'}
          </div>
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={submitting || !canSubmit}
          >
            {submitting ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      </div>

      {payDraft && (
        <PayInline
          picks={payDraft.picks}
          specialty={specialty || 'Direct booking'}
          prefillReason={reason || ''}
          onClose={() => {
            setPayDraft(null)
            setSubmitting(false)
          }}
          onSuccess={(id) => {
            setPayDraft(null)
            // just navigate; let Details handle the gentle delay/retry
            const target = `/requests/${id}?stack=${profession}`
            // slight defer so state updates flush
            setTimeout(() => nav(target, { state: { from: 'payment' } }), 0)
          }}
          profession={profession}
          searchState={state}
        />
      )}

      {activeSpecialist && (
        <div className="profile-panel" role="dialog" aria-modal="true">
          <div className="profile-panel__backdrop" onClick={() => setActiveSpecialist(null)} />
          <div className="profile-panel__sheet">
            <header className="profile-panel__head">
              <div>
                <p className="hero-kicker">Specialist</p>
                <h3>{renderName(activeSpecialist)}</h3>
                <p className="hint">{activeSpecialist.specialty || activeSpecialist.specialties?.[0] || specialty || '—'}</p>
              </div>
              <img
                src={resolvePhotoSrc(activeSpecialist)}
                alt={`${activeSpecialist.name || 'Specialist'} portrait`}
                className="results-photo profile-panel__photo"
                loading="lazy"
              />
              <button className="btn icon" onClick={() => setActiveSpecialist(null)} aria-label="Close profile">✕</button>
            </header>
            <div className="profile-panel__body">
              {(() => {
                const showMedicareInfo = profession === 'doctor'
                const med = activeSpecialist.pricing?.medicareRebate
                const medClass =
                  med === true ? 'pill-positive' : med === false ? 'pill-neutral' : 'pill-muted'
                const medLabel = med === true ? 'Yes' : med === false ? 'No' : 'Not specified'
                return (
                  <>
                  {activeSpecialist.ratingCount ? (
                    <div className="results-rating" style={{ marginBottom: '1rem' }}>
                      <RatingStarsDisplay value={Number(activeSpecialist.ratingAverage || 0)} showValue />
                      <span className="hint">
                        {`${activeSpecialist.ratingCount} review${activeSpecialist.ratingCount === 1 ? '' : 's'}`}
                      </span>
                    </div>
                  ) : null}
                  {activeSpecialist.pricing && (
                    <div className="profile-panel__section">
                      <h4>Pricing</h4>
                      <p>
                        {formatPricingRange(activeSpecialist.pricing.minAud, activeSpecialist.pricing.maxAud, activeSpecialist.pricing.currency) ? (
                          <>
                            <strong>
                              {formatPricingRange(activeSpecialist.pricing.minAud, activeSpecialist.pricing.maxAud, activeSpecialist.pricing.currency)}
                            </strong>{' '}
                            <span className="hint">(per 30 minutes)</span>
                          </>
                        ) : (
                          'Not provided'
                        )}
                      </p>
                      {showMedicareInfo && (
                        <div className="results-status-pills">
                          <span className={`pill ${medClass}`}>Medicare rebate: {medLabel}</span>
                        </div>
                      )}
                      {activeSpecialist.pricing.notes && (
                        <p className="hint" style={{ marginTop: 8 }}>{activeSpecialist.pricing.notes}</p>
                      )}
                    </div>
                  )}
                  </>
                )
              })()}
              {activeSpecialist.profile && (
                <div className="profile-panel__section">
                  <h4>About</h4>
                  <p>{activeSpecialist.profile}</p>
                </div>
              )}
              {(profileReviewsLoading || profileReviewsError || profileReviews.length > 0) && (
                <div className="profile-panel__section">
                  <h4>Reviews</h4>
                  {profileReviewsLoading && <Spinner label="Loading reviews…" />}
                  {!profileReviewsLoading && profileReviewsError && (
                    <p className="hint">{profileReviewsError}</p>
                  )}
                  {!profileReviewsLoading && !profileReviewsError && profileReviews.length > 0 && (
                    <>
                      <ul className="review-list">
                        {profileReviews.slice(0, profileReviewsVisibleCount).map((rev: any) => (
                          <li key={rev.reviewId || rev.createdAt}>
                            <div className="review-list-head">
                              <RatingStarsDisplay value={rev.rating || 0} />
                              <span className="hint">
                                {rev.reviewerFirstName || 'Client'} ·{' '}
                                {formatReviewDate(rev.slotISO, rev.createdAt, displayTz)}
                                {rev.source ? (
                                  <>
                                    {' - '}
                                    <em>{rev.source}</em>
                                  </>
                                ) : null}
                              </span>
                            </div>
                            {rev.comment ? (
                              <p className="hint" style={{ marginTop: 8 }}>
                                “{rev.comment}”
                              </p>
                            ) : (
                              <p className="hint" style={{ marginTop: 8 }}>No comment provided.</p>
                            )}
                          </li>
                        ))}
                      </ul>
                      {profileReviewsVisibleCount < profileReviews.length && !profileReviewsLoading ? (
                        <button
                          className="btn btn-outline"
                          type="button"
                          onClick={loadMoreProfileReviews}
                          disabled={profileReviewsVisibleCount >= profileReviews.length}
                          style={{ marginTop: 12 }}
                        >
                          Load more reviews
                        </button>
                      ) : null}
                      <div ref={profileReviewsSentinelRef} />
                    </>
                  )}
                </div>
              )}
              {activeSpecialist.subspecialties && activeSpecialist.subspecialties.length > 0 && (
                <div className="profile-panel__section">
                  <h4>Sub-specialties</h4>
                  <p>{activeSpecialist.subspecialties.join(', ')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
